import { useState, useEffect } from "react";
import { DateField } from "@/components/ui/date-field";
import WizardShell from "@/components/WizardShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtEur, monthLabel, fmtDate } from "@/lib/format";

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const TERM_STATUS = {
  late: { l: "Te laat", c: "bg-red-100 text-red-700" },
  partial: { l: "Deels betaald", c: "bg-orange-100 text-orange-700" },
  open: { l: "Openstaand", c: "bg-slate-100 text-slate-600" },
};
const kindLabel = (k) => (k === "lening" ? "Lening" : k === "factuur" ? "Factuur" : k === "huur" ? "Huur" : "");

export default function ProcessPaymentWizard({ open, onOpenChange, tenantId, payments = [], presetPeriod, presetPaymentId, onSaved }) {
  const [step, setStep] = useState(0);
  const [selId, setSelId] = useState("");
  const [amount, setAmount] = useState("");
  const [dateVal, setDateVal] = useState(new Date().toISOString().slice(0, 10));
  const [action, setAction] = useState("leave");
  const [customDue, setCustomDue] = useState("");
  const [loading, setLoading] = useState(false);

  const openTerms = payments
    .filter((p) => ["open", "late", "partial"].includes(p.status))
    .sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""));
  const target = payments.find((p) => p.id === selId);
  const openFor = (p) => round2(Math.max(0, (p.amount - (p.paid_amount || 0)) + (p.penalty || 0)));
  const remainingObl = target ? openFor(target) : 0;
  const shortfall = target && Number(amount) > 0 && Number(amount) < remainingObl;

  const selectTerm = (p) => {
    setSelId(p.id);
    setAmount(openFor(p).toFixed(2));
  };

  useEffect(() => {
    if (open) {
      let initial = null;
      if (presetPaymentId) initial = payments.find((p) => p.id === presetPaymentId);
      if (!initial && presetPeriod) initial = openTerms.find((p) => p.period === presetPeriod);
      if (!initial) initial = openTerms[0];
      setSelId(initial ? initial.id : "");
      setAmount(initial ? openFor(initial).toFixed(2) : "");
      setStep(0); setAction("leave"); setCustomDue("");
      setDateVal(new Date().toISOString().slice(0, 10));
    }
    // eslint-disable-next-line
  }, [open]);

  const finish = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/tenants/${tenantId}/process-payment`, {
        payment_id: selId, period: target?.period || "", amount: round2(amount) || 0, date: dateVal,
        remainder_action: shortfall ? action : "leave", custom_due_date: customDue,
      });
      let msg = "Betaling verwerkt — bevestiging verstuurd";
      if (res.data.credit_left > 0) msg += ` (tegoed ${fmtEur(res.data.credit_left)})`;
      toast.success(msg);
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Verwerken mislukt"); }
    finally { setLoading(false); }
  };

  return (
    <WizardShell open={open} onOpenChange={onOpenChange}
      title="Betaling verwerken"
      subtitle="Deel- of vooruitbetaling toewijzen aan één termijn"
      steps={["Bedrag & termijn", "Restbedrag"]}
      step={step} setStep={setStep} onFinish={finish} loading={loading}
      canNext={step !== 0 || (!!selId && Number(amount) > 0)}
      finishLabel="Betaling verwerken">
      {step === 0 && (
        <div className="space-y-4">
          <div>
            <Label className="mb-2 block">Kies de termijn waartegen je boekt</Label>
            {openTerms.length === 0 ? (
              <p className="text-sm text-slate-400">Geen openstaande termijnen.</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1" data-testid="process-term-list">
                {openTerms.map((p) => {
                  const st = TERM_STATUS[p.status] || TERM_STATUS.open;
                  const sel = selId === p.id;
                  const kl = kindLabel(p.contract_kind);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => selectTerm(p)}
                      data-testid={`process-term-${p.id}`}
                      className={`w-full text-left flex items-center justify-between gap-3 rounded-lg border p-3 transition-colors ${sel ? "border-emerald-600 bg-emerald-50/50 ring-2 ring-emerald-600/30" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`h-4 w-4 rounded-full border flex-shrink-0 ${sel ? "border-emerald-600 bg-emerald-600" : "border-slate-300"}`} />
                        <div>
                          <div className="font-medium text-slate-800 flex items-center gap-2">
                            {monthLabel(p.period)}
                            {(kl || p.contract_ovk) && <span className="text-[10px] font-normal text-slate-400 font-mono">{[kl, p.contract_ovk].filter(Boolean).join(" · ")}</span>}
                          </div>
                          <div className="text-xs text-slate-500">Vervalt {fmtDate(p.due_date)}{p.penalty > 0 ? ` · boete ${fmtEur(p.penalty)}` : ""}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-slate-800 tabular-nums">{fmtEur(openFor(p))}</div>
                        <Badge className={`${st.c} hover:${st.c} text-[10px] font-normal`}>{st.l}</Badge>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          {target && (
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600" data-testid="process-obligation">
              Te betalen voor <b>{monthLabel(target.period)}</b>{kindLabel(target.contract_kind) ? ` (${kindLabel(target.contract_kind)})` : ""}: <b className="tabular-nums">{fmtEur(remainingObl)}</b>
              <span className="text-slate-400"> = termijn {fmtEur(round2(Math.max(0, target.amount - (target.paid_amount || 0))))} + boete {fmtEur(target.penalty || 0)}</span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Ontvangen bedrag (€)</Label>
              <Input type="number" step="0.01" min="0" inputMode="decimal" data-testid="process-amount-input"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                onBlur={(e) => setAmount(e.target.value === "" ? "" : round2(e.target.value).toFixed(2))} />
            </div>
            <div><Label>Datum</Label><DateField testId="process-date-input" value={dateVal} onChange={setDateVal} /></div>
          </div>
          {Number(amount) > remainingObl && target && <div className="text-xs text-emerald-600">Meer dan openstaand → overschot wordt als vooruitbetaling toegewezen aan volgende termijnen van dezelfde overeenkomst.</div>}
        </div>
      )}
      {step === 1 && (
        <div className="space-y-4">
          {shortfall ? (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">Er blijft <b>{fmtEur(round2(remainingObl - Number(amount)))}</b> open (termijn + boete {fmtEur(target?.penalty || 0)}). Wat wil je met het restbedrag doen?</div>
              <div className="space-y-2">
                {[["leave", "Laat openstaan op deze termijn (deelbetaling)"], ["next", "Optellen bij de volgende termijn"], ["end", "Als extra termijn aan het einde toevoegen"], ["custom", "Nieuwe termijn met eigen vervaldatum"]].map(([v, l]) => (
                  <label key={v} data-testid={`remainder-${v}`} className={`flex items-center gap-2 border rounded-lg p-3 cursor-pointer text-sm ${action === v ? "ring-2 ring-blue-600/40 border-blue-600 bg-blue-50/30" : "border-slate-200"}`}>
                    <input type="radio" checked={action === v} onChange={() => setAction(v)} /> {l}
                  </label>
                ))}
              </div>
              {action === "custom" && <div><Label>Vervaldatum nieuwe termijn</Label><DateField testId="remainder-custom-due" value={customDue} onChange={setCustomDue} /></div>}
            </>
          ) : (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800">De betaling dekt de termijn volledig{Number(amount) > remainingObl ? " en wordt deels vooruitbetaald" : ""}. Geen restbedrag.</div>
          )}
        </div>
      )}
    </WizardShell>
  );
}
